import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { StyledDateRangePicker, SelectBack, Container, StyledSingleCalendar } from './styles';
import { ptBR } from 'date-fns/locale';
import { addYears, endOfYear, startOfYear } from 'date-fns';
import { defaultTheme } from 'styles/theme/default';

interface IProps {
  open: boolean,
  dateRangeLimit?: number,
  position?: "left" | "right" | "below" | "center",
  setOpen: (x: boolean) => void,
  onApplyFilter: (startDate: string, endDate: string) => void,
  onClearFilter: () => void,
  initialStartDate?: string,
  initialEndDate?: string,
}

const INITIAL_DATE = {
  startDate: new Date(),
  endDate: new Date(),
  key: 'selection'
}

export function Calendar({
  open,
  dateRangeLimit,
  position,
  setOpen,
  onApplyFilter,
  onClearFilter,
  initialStartDate,
  initialEndDate
}: IProps) {
  // Inicializa com as datas fornecidas ou com a data atual
  const getInitialDate = () => {
    if (initialStartDate && initialEndDate) {
      return {
        startDate: dayjs(initialStartDate).toDate(),
        endDate: dayjs(initialEndDate).toDate(),
        key: 'selection'
      };
    }
    return INITIAL_DATE;
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [minDate, setMinDate] = useState(endOfYear(addYears(new Date(), -3)))
  const [maxDate, setMaxDate] = useState(endOfYear(addYears(new Date(), 3)))
  const [isRangeInvalid, setIsRangeInvalid] = useState(false)

  // Atualiza as datas selecionadas quando as props iniciais mudarem ou quando o calendário for aberto
  useEffect(() => {
    if (open && initialStartDate && initialEndDate) {
      setSelectedDate({
        startDate: dayjs(initialStartDate).toDate(),
        endDate: dayjs(initialEndDate).toDate(),
        key: 'selection'
      });
    }
  }, [open, initialStartDate, initialEndDate]);

  const formatDate = (date: any) => {
    return dayjs(date).format('YYYY-MM-DD');
  }

  const initialDate = formatDate(selectedDate.startDate)
  const finalDate = formatDate(selectedDate.endDate)

  const handleApplyFilter = async () => {
    onApplyFilter(initialDate, finalDate);
    setOpen(false);
  };

  const handleClearFilter = async () => {
    resetDate();
    setSelectedDate(INITIAL_DATE);
    onClearFilter();
  }

  const resetDate = () => {
    setMinDate(startOfYear(new Date()))
    setMaxDate(endOfYear(addYears(new Date(), 3)))
  }

  const isDateRangeGreaterThan14Days = (start: number, end: number) => {
    const millisecondsInOneDay = 24 * 60 * 60 * 1000;
    const differenceInMilliseconds = Math.abs(end - start);
    const differenceInDays = differenceInMilliseconds / millisecondsInOneDay;

    return differenceInDays >= (dateRangeLimit ?? 14);
  };

  useEffect(() => {
    if (dateRangeLimit) {
      const finalDate = selectedDate.endDate
      const initialDate = selectedDate.startDate

      setIsRangeInvalid(isDateRangeGreaterThan14Days(initialDate.getTime(), finalDate.getTime()))
    }
  }, [selectedDate])

  if (!open) return null

  // Decide qual componente usar baseado no dateRangeLimit
  if (dateRangeLimit === 1) {
    return <SingleDateCalendar
      open={open}
      position={position}
      setOpen={setOpen}
      onApplyFilter={onApplyFilter}
      onClearFilter={onClearFilter}
      initialStartDate={initialStartDate}
    />;
  }

  return (
    <>
      <SelectBack className="popupBack" onClick={(e) => {
        e.stopPropagation()
        setOpen(false)
      }} />
      <Container className={`${position}`}>
        <StyledDateRangePicker
          onChange={(item: any) => setSelectedDate(item.selection)}
          moveRangeOnFirstSelection={false}
          months={1}
          ranges={[selectedDate]}
          direction="horizontal"
          locale={ptBR}
          minDate={minDate}
          maxDate={maxDate}
          rangeColors={[defaultTheme.colors['base']]}
        />
        <div className='filter'>
          <button className='limparFiltro' onClick={handleClearFilter}>Limpar Filtro</button>
          <button className='aplicarFiltro' onClick={handleApplyFilter} disabled={isRangeInvalid}>Aplicar Filtro</button>
        </div>
      </Container>
    </>
  );
}

// Novo componente para seleção de data única
function SingleDateCalendar({
  open,
  position,
  setOpen,
  onApplyFilter,
  onClearFilter,
  initialStartDate,
}: Omit<IProps, 'dateRangeLimit' | 'initialEndDate'>) {
  // Inicializa com a data fornecida ou com a data atual
  const getInitialDate = () => {
    if (initialStartDate) {
      return dayjs(initialStartDate).toDate();
    }
    return new Date();
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [minDate, setMinDate] = useState(startOfYear(new Date()));
  const [maxDate, setMaxDate] = useState(endOfYear(addYears(new Date(), 3)));

  // Atualiza a data selecionada quando a prop inicial mudar ou quando o calendário for aberto
  useEffect(() => {
    if (open && initialStartDate) {
      setSelectedDate(dayjs(initialStartDate).toDate());
    }
  }, [open, initialStartDate]);

  const formatDate = (date: Date) => {
    return dayjs(date).format('YYYY-MM-DD');
  }

  const formattedSelectedDate = formatDate(selectedDate);

  const handleApplyFilter = async () => {
    onApplyFilter(formattedSelectedDate, formattedSelectedDate);
    setOpen(false);
  };

  const handleClearFilter = async () => {
    setSelectedDate(new Date());
    onClearFilter();
  }

  if (!open) return null;

  return (
    <>
      <SelectBack className="popupBack" onClick={(e) => {
        e.stopPropagation()
        setOpen(false)
      }} />
      <Container className={`${position}`}>
        <StyledSingleCalendar
          onChange={(date: Date) => setSelectedDate(date)}
          date={selectedDate}
          locale={ptBR}
          minDate={minDate}
          maxDate={maxDate}
          color={defaultTheme.colors['base']}
        />
        <div className='filter'>
          <button className='limparFiltro' onClick={handleClearFilter}>Limpar Filtro</button>
          <button className='aplicarFiltro' onClick={handleApplyFilter}>Aplicar Filtro</button>
        </div>
      </Container>
    </>
  );
}
